import moment from 'moment'
import { ORDER_STATUS } from './enums'

export const FormatShopifyImage = (image, size = 500) => {
    if (!image) return undefined

    let imageStr
    const imageArr = image.split('.jpg')
    imageStr = `${imageArr[0]}_${size}x.jpg`
    for (let i = 1; i < imageArr.length; i++) {
        imageStr += imageArr[i]
    }
    return imageStr
}

export const parseServiceSchedule = schedule => {
    const now = moment()
    const currentDayName = now.format('dddd')
    const activeDays = schedule.filter(day => day.times !== null)

    if (activeDays.length === 0) return { isService: true, servingTimes: [] }

    let isService = false
    const timeMap = new Map()

    for (const day of activeDays) {
        for (const time of day.times) {
            const timeKey = `${time.open}-${time.close}`

            // Check if the time interval already exists
            if (!timeMap.has(timeKey)) {
                timeMap.set(timeKey, {
                    open: time.open,
                    close: time.close,
                    days: [],
                })
            }
            timeMap.get(timeKey).days.push(day.name)

            // Check if current time is within the operating hours
            if (day.name === currentDayName) {
                const openMoment = moment(time.open, 'HH:mm')
                const closeMoment = moment(time.close, 'HH:mm')

                if (now.isBetween(openMoment, closeMoment)) {
                    isService = true
                }
            }
        }
    }

    const servingTimes = Array.from(timeMap.values())

    return {
        isService,
        servingTimes,
    }
}

export const extractInventoryIds = cartItems => {
    const ids = new Set()

    for (const item of cartItems) {
        item.variants?.forEach(v => ids.add(v.inventory?.id))
        item.selections?.forEach(sel => {
            sel.options?.forEach(opt => ids.add(opt.inventory?.id))
        })
    }

    return Array.from(ids)
}

export const processOrderStatus = order => {
    if (!order?.dishes) return { lineitems: [], totalQuantity: 0, totalAmount: 0 }

    // group by dishId
    const dishGroups = new Map()

    order.dishes.forEach(dish => {
        if (!dishGroups.has(dish.dishId)) {
            dishGroups.set(dish.dishId, {
                dishId: dish.dishId,
                name: dish.name,
                image: dish.image,
                vendorName: dish.vendorName,
                stallName: dish.cafeStallName,
                items: new Map(),
                completedCount: 0,
                preparingCount: 0,
            })
        }

        // create a unique key for each variant
        dish.variants.forEach(variant => {
            // sort and serialize selections to ensure the same selections will generate the same key
            const sortedSelections = [...dish.selections].sort((a, b) => a.id.localeCompare(b.id))

            const selectionsKey = JSON.stringify(sortedSelections)
            const variantKey = `${variant.variantId}-${selectionsKey}`
            const group = dishGroups.get(dish.dishId)

            if (!group.items.has(variantKey)) {
                group.items.set(variantKey, {
                    variant: {
                        ...variant,
                        quantity: 1,
                        completedCount: variant.completedAt ? 1 : 0,
                    },
                    selections: sortedSelections,
                })
            } else {
                const existingItem = group.items.get(variantKey)
                existingItem.variant.quantity += 1
                if (variant.completedAt) {
                    existingItem.variant.completedCount += 1
                }
            }
        })
    })

    // 2. Process each group, calculate status and quantity
    const items = Array.from(dishGroups.values()).map(group => {
        let totalCompleted = 0
        let totalPreparing = 0

        // Convert Map to array
        const mergedItems = Array.from(group.items.values()).map(item => {
            const { variant, selections } = item
            totalCompleted += variant.completedCount
            totalPreparing += variant.quantity - variant.completedCount

            return {
                variant,
                selections,
            }
        })

        // Determine status
        let status
        if (order.status === ORDER_STATUS.UNPAID.toUpperCase()) {
            status = ORDER_STATUS.UNPAID
        } else {
            status = totalPreparing === 0 ? ORDER_STATUS.COMPLETED : ORDER_STATUS.PAID
        }

        return {
            ...group,
            items: mergedItems,
            completedCount: totalCompleted,
            preparingCount: totalPreparing,
            status,
        }
    })

    // 3. Calculate total quantity and amount
    const quantity = items.reduce((sum, group) => {
        return (
            sum +
            group.items.reduce((groupSum, item) => {
                return groupSum + item.variant.quantity
            }, 0)
        )
    }, 0)

    // Use summary.finalAmount from order if available, otherwise calculate from items
    const amount =
        order?.summary?.finalAmount ??
        items.reduce((sum, group) => {
            return (
                sum +
                group.items.reduce((groupSum, item) => {
                    const selectionPrice = item.selections.reduce((acc, selection) => {
                        const selectedOptions = selection.options
                        return (
                            acc +
                            selectedOptions.reduce((optionTotal, option) => optionTotal + option.sellPrice * option.quantity, 0)
                        )
                    }, 0)

                    return groupSum + (item.variant.sellPrice + selectionPrice) * item.variant.quantity
                }, 0)
            )
        }, 0)

    return {
        lineitems: items,
        totalQuantity: quantity,
        totalAmount: amount,
    }
}
